import { graphQLIdGenerator } from 'helpers/graphql-utils';
import { Steven, ELEMENT_TYPES } from 'helpers/steven';
import { useCurrentCompany, useCurrentUser } from 'javascripts/hooks';

import useProjectUsers from 'components/projects/users/hooks/useProjectUsers';
import { type PageObject } from 'data/v3/data-contracts';
import { useCreateProjectPage } from 'data/v3';
import useGetProjectContacts from 'data/v1/projects/hooks/useGetProjectContacts';
import { type ExamplePageId, getExamplePage } from '../examples/examplePages';
import mixContentWithContext from '../helpers/mixContentWithContext';

type UseCreateExamplePage = {
  projectDbId: number;
  projectTitle: string;
  onSuccess: (page: PageObject) => void;
  showInvoiceFeedbackBanner: (exampleKey: ExamplePageId) => void;
};

const useCreateExamplePage = ({
  projectDbId,
  projectTitle,
  onSuccess,
  showInvoiceFeedbackBanner,
}: UseCreateExamplePage) => {
  const projectNodeId = graphQLIdGenerator('Location', projectDbId);

  const { projectUsers } = useProjectUsers({
    projectId: projectNodeId,
  });

  const currentUser = useCurrentUser();
  const { currentCompany } = useCurrentCompany();

  const { contacts } = useGetProjectContacts({
    projectDbId,
  });

  const { mutate: createProjectPage, isPending } = useCreateProjectPage({
    projectDbId,
    onSuccess: (page) => {
      Steven.entityCrud({
        entity_id: `${page.id}`,
        entity_type: 'page',
        action: 'created',
        source: 'example',
      });

      onSuccess(page);
    },
  });

  const createExamplePage = (exampleId: ExamplePageId) => {
    const examplePage = getExamplePage({
      id: exampleId,
    });

    if (examplePage) {
      const rawContent = JSON.stringify(examplePage.body);
      const mixedContent = mixContentWithContext(rawContent, {
        currentUser,
        project: {
          name: projectTitle,
          title: projectTitle,
        },
        contact: contacts?.length ? contacts[0] : null,
        projectUsers,
        company: currentCompany,
      });

      showInvoiceFeedbackBanner(exampleId);

      createProjectPage({
        title: examplePage.title,
        tiptap_content: mixedContent,
      });

      Steven.elementClicked({
        id: 'project_page_create_page',
        content: examplePage.title,
        type: ELEMENT_TYPES.BUTTON,
        action: 'project_page_create_page_clicked',
      });
    }
  };

  return {
    createExamplePage,
    isLoading: isPending,
  };
};

export default useCreateExamplePage;
